import { useState } from "react";
import { UseFormWatch, UseFormSetValue } from "react-hook-form";
import { Check, ChevronDown, ChevronUp } from "lucide-react";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import type { CreateTripInput } from "@/lib/validations";

const AIRLINES = [
  { code: "AA", name: "American Airlines" },
  { code: "DL", name: "Delta Air Lines" },
  { code: "UA", name: "United Airlines" },
  { code: "WN", name: "Southwest Airlines" },
  { code: "NK", name: "Spirit Airlines" },
  { code: "F9", name: "Frontier Airlines" },
  { code: "B6", name: "JetBlue Airways" },
  { code: "AS", name: "Alaska Airlines" },
];

interface PreferencesAccordionProps {
  watch: UseFormWatch<CreateTripInput>;
  setValue: UseFormSetValue<CreateTripInput>;
}

interface ToggleRowProps {
  id: string;
  label: string;
  checked: boolean;
  onChange: (next: boolean) => void;
}

function ToggleRow({ id, label, checked, onChange }: ToggleRowProps) {
  return (
    <div className="flex items-center justify-between py-2">
      <Label htmlFor={id} className="cursor-pointer text-sm font-normal">
        {label}
      </Label>
      <button
        type="button"
        id={id}
        role="switch"
        aria-checked={checked}
        onClick={() => onChange(!checked)}
        className={cn(
          "relative h-6 w-11 shrink-0 rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ring-offset-background",
          checked ? "bg-foreground" : "bg-muted"
        )}
      >
        <span
          className={cn(
            "absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform",
            checked ? "translate-x-[22px]" : "translate-x-0.5"
          )}
        />
      </button>
    </div>
  );
}

export default function PreferencesAccordion({ watch, setValue }: PreferencesAccordionProps) {
  const [showMore, setShowMore] = useState(false);

  const prefs = watch("preferences");
  const carrierMode = prefs?.carrier_filter?.mode ?? "include";
  const carriers = prefs?.carrier_filter?.carriers ?? [];

  function setPref<K extends keyof NonNullable<CreateTripInput["preferences"]>>(
    key: K,
    value: NonNullable<CreateTripInput["preferences"]>[K]
  ) {
    setValue(`preferences.${key}` as const, value as never, { shouldDirty: true });
  }

  function toggleCarrier(code: string) {
    const next = carriers.includes(code)
      ? carriers.filter((c) => c !== code)
      : [...carriers, code];
    setPref("carrier_filter", { mode: carrierMode, carriers: next });
  }

  function toggleMode() {
    setPref("carrier_filter", {
      mode: carrierMode === "include" ? "exclude" : "include",
      carriers,
    });
  }

  return (
    <AccordionItem value="preferences">
      <AccordionTrigger>Preferences</AccordionTrigger>
      <AccordionContent className="space-y-5">
        {/* Carrier filter (surfaced by default) */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label className="text-sm font-medium">Airlines</Label>
            <Button type="button" variant="outline" size="sm" onClick={toggleMode}>
              {carrierMode === "include" ? "Only these" : "Exclude these"}
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            {carrierMode === "include"
              ? "Search only the airlines you pick. Leave empty for all airlines."
              : "Skip the airlines you pick."}
          </p>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4" role="group" aria-label="Airlines">
            {AIRLINES.map((airline) => {
              const selected = carriers.includes(airline.code);
              return (
                <button
                  key={airline.code}
                  type="button"
                  aria-pressed={selected}
                  onClick={() => toggleCarrier(airline.code)}
                  title={airline.name}
                  className={cn(
                    "flex items-center justify-center gap-1 rounded-md border px-2 py-2 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ring-offset-background",
                    selected
                      ? "border-primary bg-primary/15 text-accent-foreground"
                      : "border-input bg-background hover:bg-accent"
                  )}
                >
                  {selected && <Check className="h-3.5 w-3.5" aria-hidden="true" />}
                  {airline.code}
                </button>
              );
            })}
          </div>
        </div>

        {/* Direct-only (surfaced by default) */}
        <div className="border-t pt-2">
          <ToggleRow
            id="pref-direct-only"
            label="Direct flights only"
            checked={!!prefs?.direct_only}
            onChange={(v) => setPref("direct_only", v)}
          />
        </div>

        {/* More options disclosure */}
        <div className="border-t pt-2">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setShowMore((s) => !s)}
            className="text-muted-foreground"
            aria-expanded={showMore}
          >
            {showMore ? (
              <ChevronUp className="mr-1 h-4 w-4" aria-hidden="true" />
            ) : (
              <ChevronDown className="mr-1 h-4 w-4" aria-hidden="true" />
            )}
            More options
          </Button>

          {showMore && (
            <div className="mt-2 space-y-1">
              <ToggleRow
                id="pref-widebody"
                label="Prefer widebody aircraft"
                checked={!!prefs?.prefer_widebody}
                onChange={(v) => setPref("prefer_widebody", v)}
              />
              <ToggleRow
                id="pref-arrive-late"
                label="Prefer late arrivals"
                checked={!!prefs?.arrive_late}
                onChange={(v) => setPref("arrive_late", v)}
              />
              <ToggleRow
                id="pref-low-occupancy"
                label="Prefer low occupancy"
                checked={!!prefs?.prefer_low_occupancy}
                onChange={(v) => setPref("prefer_low_occupancy", v)}
              />
              <ToggleRow
                id="pref-changeable"
                label="Prefer changeable fares"
                checked={!!prefs?.prefer_changeable}
                onChange={(v) => setPref("prefer_changeable", v)}
              />

              <div className="pt-3">
                <div className="mb-1 flex items-center justify-between">
                  <Label htmlFor="pref-price" className="text-sm font-normal">
                    Price-drop alert threshold
                  </Label>
                  <span className="font-mono text-sm font-semibold text-primary">
                    ${prefs?.price_sensitivity ?? 50}
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="font-mono text-xs text-muted-foreground">$10</span>
                  <input
                    id="pref-price"
                    type="range"
                    min={10}
                    max={200}
                    step={5}
                    value={prefs?.price_sensitivity ?? 50}
                    onChange={(e) => setPref("price_sensitivity", Number(e.target.value))}
                    className="h-1.5 flex-1 accent-primary"
                  />
                  <span className="font-mono text-xs text-muted-foreground">$200</span>
                </div>
                <p className="mt-1 text-xs text-muted-foreground">
                  Alert when price drops by this amount or more.
                </p>
              </div>
            </div>
          )}
        </div>
      </AccordionContent>
    </AccordionItem>
  );
}
